/**
 * Admin Account Requests Screen - MOBILE ONLY
 * 
 * Manage pending account requests.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl, ActivityIndicator, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, Redirect } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { authApi } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';
import { AccountRequest } from '../../../shared/types/auth';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, ListCard } from '../../../components/v2/mobile';

type FilterStatus = 'pending' | 'approved' | 'denied' | 'all';

export default function AdminRequestsScreen() {
  const router = useRouter();
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';

  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [filter, setFilter] = useState<FilterStatus>('pending');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState<number | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      const status = filter === 'all' ? undefined : filter;
      const response = await authApi.getAccountRequests(status);
      setRequests(response.requests || []);
    } catch (err) {
      logger.error('Failed to fetch requests', 'admin', String(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchRequests();
  }, [fetchRequests]);

  const handleBack = useCallback(() => router.back(), [router]);

  const handleApprove = useCallback(async (request: AccountRequest) => {
    setProcessing(request.id);
    try {
      await authApi.approveAccountRequest(request.id);
      fetchRequests();
    } catch (err) {
      Alert.alert('Error', 'Failed to approve request');
      logger.error('Approve request failed', 'admin', String(err));
    } finally {
      setProcessing(null);
    }
  }, [fetchRequests]);

  const handleDeny = useCallback(async (request: AccountRequest) => {
    Alert.alert('Deny Request', `Deny account request for ${request.email}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Deny',
        style: 'destructive',
        onPress: async () => {
          setProcessing(request.id);
          try {
            await authApi.denyAccountRequest(request.id);
            fetchRequests();
          } catch (err) {
            Alert.alert('Error', 'Failed to deny request');
            logger.error('Deny request failed', 'admin', String(err));
          } finally {
            setProcessing(null);
          }
        },
      },
    ]);
  }, [fetchRequests]);

  // Redirect non-admins
  if (!isAdmin) {
    return <Redirect href="/v2/home" />;
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={color.primary} />}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Account Requests</Text>
          <View style={styles.spacer} />
        </View>

        {/* FILTER TABS */}
        <View style={styles.tabsContainer}>
          <FilterTab label="Pending" active={filter === 'pending'} onPress={() => setFilter('pending')} />
          <FilterTab label="Approved" active={filter === 'approved'} onPress={() => setFilter('approved')} />
          <FilterTab label="Denied" active={filter === 'denied'} onPress={() => setFilter('denied')} />
          <FilterTab label="All" active={filter === 'all'} onPress={() => setFilter('all')} />
        </View>

        {/* REQUEST LIST */}
        <View style={styles.section}>
          {loading ? (
            <View style={styles.loadingState}>
              <ActivityIndicator size="large" color={color.primary} />
            </View>
          ) : requests.length === 0 ? (
            <ListCard>
              <View style={styles.emptyState}>
                <Ionicons name="mail-outline" size={icon.xxl} color={color.textMuted} />
                <Text style={styles.emptyText}>No {filter === 'all' ? '' : filter} requests</Text>
              </View>
            </ListCard>
          ) : (
            <ListCard>
              {requests.map((request, index) => (
                <RequestRow
                  key={request.id}
                  request={request}
                  onApprove={() => handleApprove(request)}
                  onDeny={() => handleDeny(request)}
                  processing={processing === request.id}
                  hideBorder={index === requests.length - 1}
                />
              ))}
            </ListCard>
          )}
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// FILTER TAB
// =============================================================================

function FilterTab({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.tab, active && styles.tabActive]}>
      <Text style={[styles.tabText, active && styles.tabTextActive]}>{label}</Text>
    </Pressable>
  );
}

// =============================================================================
// REQUEST ROW
// =============================================================================

function RequestRow({ request, onApprove, onDeny, processing, hideBorder }: {
  request: AccountRequest;
  onApprove: () => void;
  onDeny: () => void;
  processing: boolean;
  hideBorder: boolean;
}) {
  const isPending = request.status === 'pending';
  const statusColor = request.status === 'approved' ? color.success 
    : request.status === 'denied' ? color.error : color.warning;

  return (
    <View style={[styles.requestRow, !hideBorder && styles.requestRowBorder]}>
      <View style={styles.requestContent}>
        <Text style={styles.requestEmail} numberOfLines={1}>{request.email}</Text>
        <Text style={styles.requestDate}>
          {new Date(request.requested_at).toLocaleDateString()}
        </Text>
      </View>
      {isPending ? (
        <View style={styles.actions}>
          {processing ? (
            <ActivityIndicator size="small" color={color.primary} />
          ) : (
            <>
              <Pressable onPress={onApprove} style={styles.approveButton}>
                <Ionicons name="checkmark" size={icon.md} color={color.success} />
              </Pressable>
              <Pressable onPress={onDeny} style={styles.denyButton}>
                <Ionicons name="close" size={icon.md} color={color.error} />
              </Pressable>
            </>
          )}
        </View>
      ) : (
        <View style={[styles.statusBadge, { backgroundColor: statusColor + '15' }]}>
          <Text style={[styles.statusText, { color: statusColor }]}>{request.status}</Text>
        </View>
      )}
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  loadingState: { paddingVertical: space.xxl, alignItems: 'center' },
  emptyState: { paddingVertical: space.xxl, alignItems: 'center', gap: space.md },
  emptyText: { fontSize: text.body.fontSize, color: color.textMuted },
  tabsContainer: { flexDirection: 'row', paddingHorizontal: space.md, marginBottom: space.lg, gap: space.xs },
  tab: { flex: 1, paddingVertical: space.sm, alignItems: 'center', borderRadius: radius.md, backgroundColor: color.backgroundSecondary },
  tabActive: { backgroundColor: color.primary },
  tabText: { fontSize: text.caption.fontSize, fontWeight: '500', color: color.textSecondary },
  tabTextActive: { color: color.textInverse },
  requestRow: { flexDirection: 'row', alignItems: 'center', padding: space.md, gap: space.sm },
  requestRowBorder: { borderBottomWidth: border.width.thin, borderBottomColor: color.divider },
  requestContent: { flex: 1 },
  requestEmail: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  requestDate: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: 2 },
  actions: { flexDirection: 'row', gap: space.sm },
  approveButton: { width: 36, height: 36, borderRadius: 18, backgroundColor: color.successLight, alignItems: 'center', justifyContent: 'center' },
  denyButton: { width: 36, height: 36, borderRadius: 18, backgroundColor: color.errorLight, alignItems: 'center', justifyContent: 'center' },
  statusBadge: { paddingHorizontal: space.sm, paddingVertical: space.xs - 1, borderRadius: radius.full },
  statusText: { fontSize: text.caption.fontSize - 1, fontWeight: '600', textTransform: 'capitalize' },
});

